import { useState } from 'react';

import {
  enforceSeparateBinary,
  findSeparateCustomFilterForDef,
  getFilterDefId,
} from '../../api/columns-api-helpers';
import {
  CustomFilterModal,
  type CustomFilterModalMode,
} from '../../components/custom-filters/CustomFilterModal';
import {
  AddCustomFilterOption,
  AddSeparateCustomFilterOption,
  CustomFilterCard,
} from '../../components/custom-filters/CustomFilterOption';
import { DataBuilderGrid } from '../../components/ui/DataBuilderGrid';
import type { DatasetInstanceConfig } from '../../data-builder.store';
import { useDataBuilderActions } from '../../data-builder.store';
import type {
  CustomFilterDefinition,
  GeneralFilters,
} from '../../datasets/deliverable-column-config';
import { useLoadedEditWarning } from '../../useLoadedEditWarning';
import { filterTouchesRoleOrSkill } from '../../utils/role-skill-detection';
import { useResolveFilterLabels } from './useResolveFilterLabels';

type GeneralFiltersSectionProps = {
  generalFilters: GeneralFilters;
  activeConfig: DatasetInstanceConfig;
  activeInstanceId: string | null;
};

type CustomFilterModalState =
  | { mode: 'add' }
  | { mode: 'add-separate'; def: CustomFilterDefinition }
  | { mode: 'edit'; index: number }
  | null;

export const GeneralFiltersSection = ({
  generalFilters,
  activeConfig,
  activeInstanceId,
}: GeneralFiltersSectionProps) => {
  const [customFilterModalState, setCustomFilterModalState] =
    useState<CustomFilterModalState>(null);
  const { addCustomFilter, updateCustomFilter, removeCustomFilter } =
    useDataBuilderActions();
  const { guardEdit } = useLoadedEditWarning();

  const customFilterDefinitions = generalFilters.customFilterDefinitions;
  const customFilterCategories = generalFilters.customFilterCategories;
  const hasCustomFilters = customFilterDefinitions.length > 0;
  const customFilters = activeConfig.customFilters;

  const regularDefs = customFilterDefinitions.filter((d) => !d.isSeparate);
  const separateDefs = customFilterDefinitions.filter((d) => d.isSeparate);
  const pendingSeparateDefs = separateDefs.filter(
    (d) => findSeparateCustomFilterForDef(d, customFilters) === undefined
  );
  const hasRegularDefs = regularDefs.length > 0;

  useResolveFilterLabels({
    filters: customFilters,
    filterDefinitions: customFilterDefinitions,
    onUpdateFilter: (index, filter) => {
      if (activeInstanceId !== null) {
        updateCustomFilter(activeInstanceId, index, filter);
      }
    },
  });
  const editingFilter =
    customFilterModalState?.mode === 'edit'
      ? customFilters[customFilterModalState.index]
      : undefined;

  const modalMode: CustomFilterModalMode = (() => {
    if (customFilterModalState?.mode === 'add-separate') {
      return { kind: 'locked', def: customFilterModalState.def };
    }
    if (editingFilter !== undefined) {
      const lockedSeparateDef = separateDefs.find(
        (def) =>
          findSeparateCustomFilterForDef(def, [editingFilter]) !== undefined
      );
      if (lockedSeparateDef !== undefined) {
        return { kind: 'locked', def: lockedSeparateDef };
      }
    }
    return {
      kind: 'free',
      defs: regularDefs,
      categories: customFilterCategories,
    };
  })();

  return (
    <>
      <DataBuilderGrid columns="responsive">
        {customFilters.map((filter, index) => (
          <CustomFilterCard
            key={filter.id}
            filter={filter}
            onClick={() => {
              const roleOrSkill = filterTouchesRoleOrSkill(filter);
              const open = () =>
                setCustomFilterModalState({ mode: 'edit', index });
              if (roleOrSkill !== null) {
                guardEdit({ kind: roleOrSkill }, open);
                return;
              }
              open();
            }}
            onRemove={() => {
              if (activeInstanceId !== null) {
                removeCustomFilter(activeInstanceId, index);
              }
            }}
          />
        ))}

        {pendingSeparateDefs.map((def) => (
          <AddSeparateCustomFilterOption
            key={getFilterDefId(def)}
            filterName={def.name}
            testIdSuffix={getFilterDefId(def)}
            onClick={() =>
              setCustomFilterModalState({ mode: 'add-separate', def })
            }
          />
        ))}

        {hasRegularDefs && (
          <AddCustomFilterOption
            onClick={() => setCustomFilterModalState({ mode: 'add' })}
          />
        )}
      </DataBuilderGrid>

      {hasCustomFilters && (
        <CustomFilterModal
          isOpen={customFilterModalState !== null}
          onClose={() => setCustomFilterModalState(null)}
          mode={modalMode}
          existingFilter={editingFilter}
          existingFilterNames={customFilters
            .filter((_, i) =>
              customFilterModalState?.mode === 'edit'
                ? i !== customFilterModalState.index
                : true
            )
            .map((filter) => filter.name)}
          onSubmit={(filter) => {
            if (activeInstanceId === null) {
              return;
            }
            const safeFilter = enforceSeparateBinary(
              filter,
              customFilterDefinitions
            );
            if (customFilterModalState?.mode === 'edit') {
              updateCustomFilter(
                activeInstanceId,
                customFilterModalState.index,
                safeFilter
              );
              return;
            }
            addCustomFilter(activeInstanceId, safeFilter);
          }}
        />
      )}
    </>
  );
};
